import type { Block, InlineRun } from './types';

function extractInlineRuns(node: Node): InlineRun[] {
  const runs: InlineRun[] = [];

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    if (text) {
      runs.push({ text });
    }
    return runs;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return runs;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  // Inline formatting tags
  if (tag === 'strong' || tag === 'b') {
    const childRuns = extractChildRuns(el);
    for (const run of childRuns) {
      run.bold = true;
    }
    return childRuns;
  }
  if (tag === 'em' || tag === 'i') {
    const childRuns = extractChildRuns(el);
    for (const run of childRuns) {
      run.italic = true;
    }
    return childRuns;
  }
  if (tag === 'u') {
    const childRuns = extractChildRuns(el);
    for (const run of childRuns) {
      run.underline = true;
    }
    return childRuns;
  }
  if (tag === 'br') {
    return [{ text: '\n' }];
  }

  // For other inline elements (span, a, etc.), just recurse
  return extractChildRuns(el);
}

function extractChildRuns(el: Element): InlineRun[] {
  const runs: InlineRun[] = [];
  for (const child of Array.from(el.childNodes)) {
    runs.push(...extractInlineRuns(child));
  }
  return runs;
}

function parseListItems(listEl: Element): InlineRun[][] {
  const items: InlineRun[][] = [];
  for (const child of Array.from(listEl.children)) {
    if (child.tagName.toLowerCase() === 'li') {
      items.push(extractChildRuns(child));
    }
  }
  return items;
}

function parseTableRows(tableEl: Element): string[][] {
  const rows: string[][] = [];
  const allRows = tableEl.querySelectorAll('tr');
  for (const tr of Array.from(allRows)) {
    const cells: string[] = [];
    const tds = tr.querySelectorAll('td, th');
    for (const td of Array.from(tds)) {
      cells.push((td.textContent || '').trim());
    }
    if (cells.length > 0) {
      rows.push(cells);
    }
  }
  return rows;
}

export function htmlToBlocks(html: string): Block[] {
  if (!html || !html.trim()) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks: Block[] = [];

  function processNode(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').trim();
      if (text) {
        blocks.push({ type: 'paragraph', runs: [{ text }] });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // Headings
    if (/^h[1-6]$/.test(tag)) {
      const level = Math.min(parseInt(tag[1]), 3) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, text: (el.textContent || '').trim() });
      return;
    }

    // Paragraphs
    if (tag === 'p') {
      const runs = extractChildRuns(el);
      if (runs.length > 0 && runs.some(r => r.text.trim())) {
        blocks.push({ type: 'paragraph', runs });
      }
      return;
    }

    // Lists — handle nested <ul>/<ol> inside <li> as separate blocks
    if (tag === 'ul' || tag === 'ol') {
      const ordered = tag === 'ol';
      let currentItems: InlineRun[][] = [];

      const flushItems = () => {
        if (currentItems.length > 0) {
          blocks.push({ type: 'list', ordered, items: [...currentItems] });
          currentItems = [];
        }
      };

      for (const child of Array.from(el.children)) {
        if (child.tagName.toLowerCase() !== 'li') continue;

        const runs: InlineRun[] = [];
        const nestedLists: Element[] = [];

        for (const node of Array.from(child.childNodes)) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const nodeEl = node as Element;
            const nodeTag = nodeEl.tagName.toLowerCase();
            if (nodeTag === 'ul' || nodeTag === 'ol') {
              nestedLists.push(nodeEl);
              continue;
            }
          }
          runs.push(...extractInlineRuns(node));
        }

        if (runs.some(r => r.text.trim())) {
          currentItems.push(runs);
        }

        if (nestedLists.length > 0) {
          flushItems();
          for (const nested of nestedLists) processNode(nested);
        }
      }

      flushItems();
      return;
    }

    // Tables
    if (tag === 'table') {
      const rows = parseTableRows(el);
      if (rows.length > 0) {
        blocks.push({ type: 'table', rows });
      }
      return;
    }

    // Divs, sections, etc. - recurse into children
    for (const child of Array.from(el.childNodes)) {
      processNode(child);
    }
  }

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }

  return blocks;
}

/** Strip HTML to plain text (ohne Struktur — nur für einfache Fälle) */
export function htmlToPlainText(html: string): string {
  if (!html || !html.trim()) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return (doc.body.textContent || '').trim();
}

function runsToPlain(runs: InlineRun[]): string {
  return runs.map(r => r.text).join('');
}

/**
 * Rendert die Block-Zwischenrepräsentation strukturiert als Klartext:
 * Überschriften auf eigener Zeile, Absätze durch Leerzeilen getrennt,
 * Listen mit "- "/"1. "-Aufzählung, Tabellenzeilen mit " | " getrennt.
 * Erhält damit die Struktur (anders als htmlToPlainText).
 */
export function blocksToPlainText(blocks: Block[]): string {
  const out: string[] = [];
  for (const block of blocks) {
    switch (block.type) {
      case 'heading':
        out.push('', block.text.trim(), '');
        break;
      case 'paragraph': {
        const t = runsToPlain(block.runs);
        if (t.trim()) { out.push(t); out.push(''); }
        break;
      }
      case 'list':
        block.items.forEach((item, i) => {
          const bullet = block.ordered ? `${i + 1}. ` : '- ';
          out.push('  ' + bullet + runsToPlain(item).replace(/\n+/g, ' ').trim());
        });
        out.push('');
        break;
      case 'table':
        for (const row of block.rows) out.push('  ' + row.join('  |  '));
        out.push('');
        break;
    }
  }
  // Mehrfache Leerzeilen zusammenfassen
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
